import { useState, useEffect } from "react";
import { CheckCircle, XCircle, Clock, RefreshCw, Package, Edit2, Trash2, Loader2, CalendarClock } from "lucide-react";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import API from "@/lib/api";
import { toast } from "sonner";
import { ADMIN } from "@/constants/testIds";

const STATUS_COLORS = {
  pending: "bg-amber-50 text-amber-700 border-amber-200",
  trial: "bg-blue-50 text-blue-700 border-blue-200",
  active: "bg-emerald-50 text-emerald-700 border-emerald-200",
  completed: "bg-emerald-50 text-emerald-700 border-emerald-200",
  rejected: "bg-red-50 text-red-700 border-red-200",
  trial_expired: "bg-red-50 text-red-600 border-red-200",
};

const EMPTY_PKG = { name: "", credits: "", price: "", bonus_credits: "", featured: false, status: "active" };

export default function AdminFinance() {
  const [payments, setPayments] = useState([]);
  const [packages, setPackages] = useState([]);
  const [subscriptions, setSubscriptions] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [tab, setTab] = useState("payments");

  const [rejectModal, setRejectModal] = useState(false);
  const [rejectSubModal, setRejectSubModal] = useState(false);
  const [selected, setSelected] = useState(null);
  const [selectedSub, setSelectedSub] = useState(null);
  const [rejectReason, setRejectReason] = useState("");

  const [pkgModal, setPkgModal] = useState(false);
  const [pkgForm, setPkgForm] = useState(EMPTY_PKG);
  const [editPkg, setEditPkg] = useState(null);
  const [saving, setSaving] = useState(false);

  useEffect(() => { loadData(); }, [tab]);

  const loadData = async () => {
    setLoading(true);
    try {
      if (tab === "subscriptions") {
        const res = await API.get("/subscriptions/admin/all?limit=50");
        setSubscriptions(res.data.subscriptions || []);
      } else {
        const [pmts, pkgs] = await Promise.all([
          API.get("/payments?limit=50"),
          API.get("/payments/packages"),
        ]);
        setPayments(pmts.data.payments || []);
        setTotal(pmts.data.total || 0);
        setPackages(pkgs.data.packages || []);
      }
    } catch { toast.error("Failed to load finance data"); }
    finally { setLoading(false); }
  };

  const approve = async (paymentId) => {
    try {
      await API.put(`/payments/${paymentId}/approve`);
      toast.success("Payment approved & credits added");
      loadData();
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
  };

  const reject = async () => {
    if (!selected) return;
    try {
      await API.put(`/payments/${selected.payment_id}/reject`, { reason: rejectReason });
      toast.success("Payment rejected");
      setRejectModal(false);
      setRejectReason("");
      loadData();
    } catch { toast.error("Failed"); }
  };

  const approveSub = async (subId) => {
    try {
      const res = await API.put(`/subscriptions/admin/${subId}/approve`);
      toast.success(res.data.message || "Subscription approved");
      loadData();
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed to approve"); }
  };

  const rejectSub = async () => {
    if (!selectedSub) return;
    try {
      await API.put(`/subscriptions/admin/${selectedSub._id}/reject`, { reason: rejectReason });
      toast.success("Subscription rejected and Sender IDs suspended");
      setRejectSubModal(false);
      setSelectedSub(null);
      setRejectReason("");
      loadData();
    } catch { toast.error("Failed to reject subscription"); }
  };

  const createPackage = async () => {
    setSaving(true);
    try {
      if (editPkg) {
        await API.put(`/payments/packages/${editPkg._id}`, {
          name: pkgForm.name,
          credits: parseInt(pkgForm.credits),
          price: Math.round(parseFloat(pkgForm.price) * 100),
          bonus_credits: parseInt(pkgForm.bonus_credits || "0"),
          featured: pkgForm.featured,
          status: pkgForm.status,
        });
        toast.success("Package updated");
      } else {
        await API.post("/payments/packages", {
          name: pkgForm.name,
          credits: parseInt(pkgForm.credits),
          price: Math.round(parseFloat(pkgForm.price) * 100),
          bonus_credits: parseInt(pkgForm.bonus_credits || "0"),
          featured: pkgForm.featured,
          status: "active",
        });
        toast.success("Package created");
      }
      setPkgModal(false);
      setEditPkg(null);
      setPkgForm(EMPTY_PKG);
      loadData();
    } catch { toast.error("Failed to save package"); }
    finally { setSaving(false); }
  };

  const openEditPkg = (p) => {
    setEditPkg(p);
    setPkgForm({
      name: p.name,
      credits: String(p.credits),
      price: String((p.price || 0) / 100),
      bonus_credits: String(p.bonus_credits || "0"),
      featured: p.featured || false,
      status: p.status || "active",
    });
    setPkgModal(true);
  };

  const deletePackage = async (id) => {
    if (!window.confirm("Delete this package? Customers won't be able to buy it anymore.")) return;
    try {
      await API.delete(`/payments/packages/${id}`);
      toast.success("Package deleted");
      loadData();
    } catch { toast.error("Failed to delete package"); }
  };

  return (
    <div className="space-y-5">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-extrabold text-slate-900">Finance</h1>
          <p className="text-sm text-slate-500 mt-1">Payments, packages and subscriptions</p>
        </div>
        <Button onClick={loadData} variant="outline" size="sm" className="border-slate-300 text-slate-600 h-9 hover:bg-slate-50">
          <RefreshCw size={14} className="mr-2" /> Refresh
        </Button>
      </div>

      {/* Tabs */}
      <div className="flex gap-1 bg-slate-100 rounded-lg p-1 w-fit">
        {["payments", "packages", "subscriptions"].map((t) => (
          <button key={t} onClick={() => setTab(t)}
            className={`px-4 py-1.5 rounded-md text-sm font-medium capitalize transition-colors ${tab === t ? "bg-white text-slate-900 shadow-sm" : "text-slate-600 hover:text-slate-900"}`}
            data-testid={`finance-tab-${t}`}>
            {t}
          </button>
        ))}
      </div>

      {/* ── Payments tab ── */}
      {tab === "payments" && (
        <div className="bg-white border border-slate-200 rounded-xl overflow-hidden shadow-sm">
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead>
                <tr className="bg-slate-50 border-b border-slate-200 text-xs text-slate-500 uppercase tracking-wider">
                  <th className="text-left px-5 py-3.5 font-semibold">Customer</th>
                  <th className="text-left px-5 py-3.5 font-semibold hidden sm:table-cell">Package</th>
                  <th className="text-left px-5 py-3.5 font-semibold">Amount</th>
                  <th className="text-left px-5 py-3.5 font-semibold">Credits</th>
                  <th className="text-left px-5 py-3.5 font-semibold">Method</th>
                  <th className="text-left px-5 py-3.5 font-semibold">Status</th>
                  <th className="text-left px-5 py-3.5 font-semibold">Actions</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100">
                {loading
                  ? [...Array(4)].map((_, i) => <tr key={i}><td colSpan={7}><div className="h-10 bg-slate-100 animate-pulse mx-5 my-2 rounded" /></td></tr>)
                  : payments.length === 0
                  ? <tr><td colSpan={7} className="px-5 py-10 text-center text-slate-400 text-sm">No payments yet</td></tr>
                  : payments.map((p) => (
                    <tr key={p._id} className="hover:bg-slate-50 transition-colors">
                      <td className="px-5 py-3">
                        <div className="text-sm text-slate-900 font-medium">{p.user_name}</div>
                        <div className="text-xs text-slate-500">{p.user_email}</div>
                      </td>
                      <td className="px-5 py-3 hidden sm:table-cell text-sm text-slate-700">{p.package_name}</td>
                      <td className="px-5 py-3 text-sm font-mono font-semibold text-slate-900">₦{((p.amount || 0) / 100).toLocaleString()}</td>
                      <td className="px-5 py-3 text-sm font-mono text-[#0c1e41] font-semibold">{p.credits?.toLocaleString()}</td>
                      <td className="px-5 py-3 text-xs text-slate-500 capitalize">{(p.method || p.payment_method || "—").replace("_", " ")}</td>
                      <td className="px-5 py-3">
                        <Badge variant="outline" className={`text-xs ${STATUS_COLORS[p.status] || "bg-slate-50 text-slate-600 border-slate-200"}`}>{p.status}</Badge>
                      </td>
                      <td className="px-5 py-3">
                        {p.status === "pending" && (
                          <div className="flex gap-2">
                            <button data-testid={ADMIN.approvePaymentButton} onClick={() => approve(p.payment_id)} className="text-xs text-emerald-700 hover:text-emerald-800 px-2 py-1 bg-emerald-50 hover:bg-emerald-100 border border-emerald-200 rounded font-medium transition-colors">Approve</button>
                            <button data-testid={ADMIN.rejectPaymentButton} onClick={() => { setSelected(p); setRejectModal(true); }} className="text-xs text-red-700 hover:text-red-800 px-2 py-1 bg-red-50 hover:bg-red-100 border border-red-200 rounded font-medium transition-colors">Reject</button>
                          </div>
                        )}
                      </td>
                    </tr>
                  ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* ── Packages tab ── */}
      {tab === "packages" && (
        <div>
          <div className="flex justify-end mb-4">
            <Button onClick={() => { setEditPkg(null); setPkgForm(EMPTY_PKG); setPkgModal(true); }} size="sm" className="bg-[#0c1e41] hover:bg-[#091629] h-9">
              <Package size={14} className="mr-2" /> New Package
            </Button>
          </div>
          {packages.length === 0 && !loading ? (
            <div className="text-center py-20 text-slate-400 text-sm">No packages yet. Create one to let customers buy credits.</div>
          ) : (
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
              {packages.map((p) => (
                <div key={p._id} className={`bg-white border-2 rounded-xl p-5 shadow-sm relative ${p.featured ? "border-[#0c1e41]" : "border-slate-200"}`}>
                  {p.featured && <Badge className="mb-3 bg-[#0c1e41] text-white text-xs">Popular</Badge>}
                  <div className="absolute top-4 right-4 flex gap-1">
                    <button onClick={() => openEditPkg(p)} className="p-1.5 text-slate-400 hover:text-[#0c1e41] hover:bg-[#eef1f8] rounded-lg transition-colors" title="Edit package" data-testid="edit-package-btn"><Edit2 size={13} /></button>
                    <button onClick={() => deletePackage(p._id)} className="p-1.5 text-slate-400 hover:text-red-600 hover:bg-red-50 rounded-lg transition-colors" title="Delete package" data-testid="delete-package-btn"><Trash2 size={13} /></button>
                  </div>
                  <div className="text-base font-bold text-slate-900 mb-1 pr-16">{p.name}</div>
                  <div className="text-2xl font-extrabold font-mono text-[#0c1e41] mb-3">₦{((p.price || 0) / 100).toLocaleString()}</div>
                  <div className="text-sm text-slate-600">{p.credits.toLocaleString()} Credits</div>
                  {p.bonus_credits > 0 && <div className="text-xs text-emerald-700 font-semibold mt-1">+{p.bonus_credits} bonus credits</div>}
                  <div className="flex items-center gap-2 mt-3">
                    <Badge variant="outline" className={`text-xs ${p.status === "active" ? "bg-emerald-50 text-emerald-700 border-emerald-200" : "bg-slate-100 text-slate-600 border-slate-200"}`}>{p.status}</Badge>
                    <span className="text-xs text-slate-400">{((p.price || 0) / 100 / p.credits * 1000).toFixed(1)} ₦/1000 credits</span>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      {/* ── Subscriptions tab ── */}
      {tab === "subscriptions" && (
        <div className="bg-white border border-slate-200 rounded-xl overflow-hidden shadow-sm">
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead>
                <tr className="bg-slate-50 border-b border-slate-200 text-xs text-slate-500 uppercase tracking-wider">
                  <th className="text-left px-5 py-3.5 font-semibold">Customer</th>
                  <th className="text-left px-5 py-3.5 font-semibold">Plan</th>
                  <th className="text-left px-5 py-3.5 font-semibold hidden md:table-cell">Amount</th>
                  <th className="text-left px-5 py-3.5 font-semibold hidden sm:table-cell">Trial Ends</th>
                  <th className="text-left px-5 py-3.5 font-semibold">Status</th>
                  <th className="text-left px-5 py-3.5 font-semibold">Actions</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100">
                {loading
                  ? [...Array(4)].map((_, i) => <tr key={i}><td colSpan={6}><div className="h-10 bg-slate-100 animate-pulse mx-5 my-2 rounded" /></td></tr>)
                  : subscriptions.length === 0
                  ? <tr><td colSpan={6} className="px-5 py-10 text-center text-slate-400 text-sm">No subscriptions yet</td></tr>
                  : subscriptions.map((s) => (
                    <tr key={s._id} className="hover:bg-slate-50 transition-colors">
                      <td className="px-5 py-3">
                        <div className="text-sm text-slate-900 font-medium">{s.user_email}</div>
                        <div className="text-xs text-slate-400 mt-0.5 capitalize">{s.billing_cycle}</div>
                      </td>
                      <td className="px-5 py-3">
                        <div className="text-sm font-semibold text-slate-900">{s.plan_name}</div>
                        <div className="text-xs text-slate-400">{s.sender_id_limit === -1 ? "Unlimited" : s.sender_id_limit} Sender ID{s.sender_id_limit !== 1 ? "s" : ""}</div>
                      </td>
                      <td className="px-5 py-3 hidden md:table-cell text-sm font-mono font-semibold text-slate-900">
                        ₦{((s.price || 0) / 100).toLocaleString()}
                      </td>
                      <td className="px-5 py-3 hidden sm:table-cell">
                        {s.trial_ends_at ? (
                          <span className="flex items-center gap-1 text-xs text-slate-500">
                            <CalendarClock size={11} />
                            {new Date(s.trial_ends_at).toLocaleDateString("en-NG")}
                          </span>
                        ) : "—"}
                      </td>
                      <td className="px-5 py-3">
                        <Badge variant="outline" className={`text-xs capitalize ${STATUS_COLORS[s.status] || "bg-slate-50 text-slate-600 border-slate-200"}`}>
                          {s.status?.replace("_", " ")}
                        </Badge>
                      </td>
                      <td className="px-5 py-3">
                        {(s.status === "trial" || s.status === "pending") && (
                          <div className="flex gap-2">
                            <button
                              data-testid={`approve-sub-${s._id}`}
                              onClick={() => approveSub(s._id)}
                              className="text-xs text-emerald-700 hover:text-emerald-800 px-2 py-1 bg-emerald-50 hover:bg-emerald-100 border border-emerald-200 rounded font-medium transition-colors">
                              Approve
                            </button>
                            <button
                              data-testid={`reject-sub-${s._id}`}
                              onClick={() => { setSelectedSub(s); setRejectReason(""); setRejectSubModal(true); }}
                              className="text-xs text-red-700 hover:text-red-800 px-2 py-1 bg-red-50 hover:bg-red-100 border border-red-200 rounded font-medium transition-colors">
                              Reject
                            </button>
                          </div>
                        )}
                      </td>
                    </tr>
                  ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* ── Modals ── */}

      {/* Reject Payment */}
      <Dialog open={rejectModal} onOpenChange={setRejectModal}>
        <DialogContent className="bg-white border-slate-200 max-w-sm">
          <DialogHeader><DialogTitle className="text-slate-900">Reject Payment</DialogTitle></DialogHeader>
          <div className="space-y-4 mt-2">
            <div className="space-y-2">
              <Label className="text-slate-700 text-sm">Reason for rejection</Label>
              <Input value={rejectReason} onChange={(e) => setRejectReason(e.target.value)} placeholder="Payment could not be verified" className="bg-white border-slate-300 text-slate-900 h-10" />
            </div>
            <Button onClick={reject} className="w-full bg-red-600 hover:bg-red-700">Confirm Reject</Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Reject Subscription */}
      <Dialog open={rejectSubModal} onOpenChange={setRejectSubModal}>
        <DialogContent className="bg-white border-slate-200 max-w-sm">
          <DialogHeader><DialogTitle className="text-slate-900">Reject Subscription</DialogTitle></DialogHeader>
          <div className="space-y-4 mt-2">
            <div className="bg-amber-50 border border-amber-200 rounded-lg p-3 text-xs text-amber-700">
              Rejecting will end the trial and <strong>suspend all Sender IDs</strong> for <strong>{selectedSub?.user_email}</strong>.
            </div>
            <div className="space-y-2">
              <Label className="text-slate-700 text-sm">Reason (optional)</Label>
              <Input value={rejectReason} onChange={(e) => setRejectReason(e.target.value)} placeholder="Payment could not be verified" className="bg-white border-slate-300 text-slate-900 h-10" />
            </div>
            <Button onClick={rejectSub} className="w-full bg-red-600 hover:bg-red-700" data-testid="confirm-reject-sub-btn">Confirm Reject</Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* New/Edit Package */}
      <Dialog open={pkgModal} onOpenChange={(o) => { setPkgModal(o); if (!o) { setEditPkg(null); setPkgForm(EMPTY_PKG); } }}>
        <DialogContent className="bg-white border-slate-200 max-w-sm">
          <DialogHeader>
            <DialogTitle className="text-slate-900">{editPkg ? "Edit Package" : "Create Credit Package"}</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 mt-2">
            {[["name","Package Name","Starter"],["credits","Credits","100"],["price","Price (₦)","500"],["bonus_credits","Bonus Credits","0"]].map(([k,l,p]) => (
              <div key={k} className="space-y-2">
                <Label className="text-slate-700 text-sm">{l}</Label>
                <Input value={pkgForm[k]} onChange={(e) => setPkgForm({ ...pkgForm, [k]: e.target.value })} placeholder={p} className="bg-white border-slate-300 text-slate-900 h-10 placeholder:text-slate-400" />
              </div>
            ))}
            <div className="flex items-center justify-between py-2">
              <div>
                <p className="text-sm font-medium text-slate-900">Featured / Popular</p>
                <p className="text-xs text-slate-500">Highlight with a "Popular" badge</p>
              </div>
              <button onClick={() => setPkgForm((f) => ({ ...f, featured: !f.featured }))}
                className={`w-10 h-5 rounded-full transition-colors relative ${pkgForm.featured ? "bg-[#0c1e41]" : "bg-slate-300"}`}>
                <span className={`absolute top-0.5 w-4 h-4 bg-white rounded-full shadow transition-transform ${pkgForm.featured ? "translate-x-5" : "translate-x-0.5"}`} />
              </button>
            </div>
            {editPkg && (
              <div className="space-y-2">
                <Label className="text-slate-700 text-sm">Status</Label>
                <select value={pkgForm.status} onChange={(e) => setPkgForm({ ...pkgForm, status: e.target.value })}
                  className="w-full h-10 bg-white border border-slate-300 text-slate-900 rounded-md px-3 text-sm focus:outline-none focus:border-[#0c1e41]">
                  <option value="active">Active</option>
                  <option value="inactive">Inactive</option>
                </select>
              </div>
            )}
            <Button onClick={createPackage} disabled={saving} className="w-full bg-[#0c1e41] hover:bg-[#091629]" data-testid="save-package-btn">
              {saving ? <><Loader2 size={14} className="animate-spin mr-2" />Saving...</> : (editPkg ? "Update Package" : "Create Package")}
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
